import { Card, CardBody, CardHeader } from '@nextui-org/react'
import { FC } from 'react'
import { useTankInfoModalStore } from '../../../store/tankInfo/useTankInfoModalStore'
import { CardNameLiteral, GaugeSetting } from '../../../types/apiResponse/tankInfoSettingTypes'
import { getCardValue } from '../../../types/mappers/tankInfo.mapper'
import { convertMetric } from '../../../utils/metricConvert'
import { makeTitle } from '../../../utils/tankInfo'
import MeasureGauge from '../MeasureGauge'
import { NoContent } from './NoContent'

export type GaugeProps = {
  tankId: string
  cardName: CardNameLiteral
  gaugeSetting: GaugeSetting
}

export const GaugeCard: FC<GaugeProps> = ({ tankId, cardName, gaugeSetting }) => {
  const cardValues = useTankInfoModalStore((state) => state.cardValues)

  // Store에서 이미 계산된 카드 값 사용
  const curValue = cardValues ? getCardValue(cardValues, cardName as any) : undefined
  const { val, metric } = convertMetric(curValue, cardName, gaugeSetting?.metric ?? '')

  const isStationary =
    curValue === undefined ||
    curValue === null ||
    (curValue > gaugeSetting.minWarning && curValue < gaugeSetting.maxWarning)
  const { title, subTitle } = makeTitle(cardName)

  return (
    <Card
      className={`
        rounded-lg col-span-1
        w-full md:w-auto
        h-[140px] md:h-[8.75rem]
        p-4 md:p-5
        shadow-card
        ${isStationary ? 'border' : 'border-2'}
        ${isStationary ? 'border-tdpldarkgrey/60' : 'border-tdplmaroon'}
        ${isStationary ? 'bg-tdplbisque' : 'bg-tdplmaroon/10'}
      `}
      data-card-type="gauge"
      data-testid={`gauge-card-${cardName}`}
    >
      <CardHeader className="flex flex-row items-start gap-2 p-0 mb-4 min-h-[1.375rem]">
        <p
          className={`text-base font-bold ${isStationary ? 'text-tdpldarkgrey' : 'text-tdplmaroon'}`}
        >
          {title}
        </p>
        {subTitle && (
          <p className={`text-base ${isStationary ? 'text-tdplmiddlegrey' : 'text-tdplmaroon/30'}`}>
            {subTitle}
          </p>
        )}
      </CardHeader>

      <CardBody className="p-0 overflow-visible">
        {val === '---' ? (
          <NoContent />
        ) : (
          <div className="flex flex-row justify-between items-center gap-4 min-h-[4rem]">
            {/* Left: Value Display */}
            <div className="flex flex-col items-start flex-1">
              <p
                className={`text-base md:text-xl font-bold leading-tight ${
                  isStationary ? 'text-tdplprussian' : 'text-tdplmaroon'
                }`}
              >
                {val}
              </p>
              <p className={`text-sm ${isStationary ? 'text-tdplprussian' : 'text-tdplmaroon'}`}>
                {metric ? metric : null}
              </p>
            </div>

            {/* Right: Gauge Chart - Fixed Container */}
            <div className="w-[100px] h-[65px] flex-shrink-0">
              <MeasureGauge
                value={curValue}
                minWarning={gaugeSetting.minWarning}
                maxWarning={gaugeSetting.maxWarning}
                minLimit={gaugeSetting.minLimit}
                maxLimit={gaugeSetting.maxLimit}
              />
            </div>
          </div>
        )}
      </CardBody>
    </Card>
  )
}
